/**
 * `LinkWithClickToEdit` — extends the canonical TipTap `Link` extension
 * with a ProseMirror click handler that opens the existing
 * `<LinkEditorPopover>` for the clicked link's range.
 *
 * Plain click on a rendered `<a>` inside the editor:
 *   - The default browser navigation is suppressed (`Link.configure({
 *     openOnClick: false })` already does this — the Link extension's
 *     `addProseMirrorPlugins()` blocks the click from doing anything).
 *   - This extension dispatches `wiki:open-link-popover` with detail
 *     describing the clicked link mark's range + existing `href`. The
 *     MarkdownEditor's existing event listener (in `MarkdownEditor.tsx`)
 *     handles the rest: prefills the URL field, on submit replaces the
 *     range with the updated link.
 *
 * Cmd/Ctrl+click and middle-click are intentionally NOT handled here —
 * those should keep the native open-in-new-tab behavior. We only fire
 * when the click is a plain left-click without modifiers.
 *
 * Why not extend the TipTap Link extension via `Link.extend({ ... })`
 * inline at the extensions.ts call site? Keeping the plugin out of
 * extensions.ts isolates the `prosemirror-state`/`prosemirror-view`
 * dependency from the editor-config file. The click handler also
 * needs to know how to resolve the link mark's range — that's a fair
 * amount of code worth a dedicated module + the comments to explain
 * the range-resolution invariant.
 */

import Link, { isAllowedUri } from '@tiptap/extension-link'
import { Plugin } from '@tiptap/pm/state'

import { linkAutolinkPunctuation } from './link-autolink-punctuation'
import { linkAutolinkRevalidate } from './link-autolink-revalidate'

/** Event name shared with `slash-commands.ts → OPEN_LINK_POPOVER_EVENT`. */
const OPEN_LINK_POPOVER_EVENT = 'wiki:open-link-popover'

/**
 * Detail shape for the `link-click` source. Mirrors the existing
 * slash-menu detail (`slash-commands.ts → LinkPopoverEventDetail`) but
 * uses the discriminator `source: 'link-click'` so the listener can
 * branch on intent: slash-menu inserts new content, link-click edits
 * the existing mark in place.
 */
export interface LinkClickPopoverEventDetail {
  source: 'link-click'
  /** Existing href the click landed on. */
  existingHref: string
  /** Doc position range covered by the link mark. */
  markFrom: number
  markTo: number
  /** Anchor rect for the popover — bounding box of the clicked text. */
  anchorRect: { left: number; top: number; right: number; bottom: number }
}

/**
 * Did this click land on a rendered `<a>` inside the editor?
 *
 * Exported for the regression test. The bug this guards against is easy
 * to reintroduce because the symptom looks like a caret problem rather
 * than a hit-testing one: a link at the END of a list item or paragraph
 * hijacked every click in the remaining whitespace of its own block, and
 * typing a trailing space "fixed" it — which reads as a ProseMirror
 * position quirk, so the tempting patch is to fiddle with the position
 * math rather than to ask what the user actually clicked.
 *
 * `target` may be a text node (some browsers report the text node rather
 * than its element), hence the `parentElement` hop before `closest`.
 */
export function isClickOnLinkAnchor(target: EventTarget | null, editorDom: Element): boolean {
  const element =
    target instanceof Element ? target : target instanceof Node ? target.parentElement : null
  const anchor = element?.closest('a') ?? null
  return anchor !== null && editorDom.contains(anchor)
}

export const LinkWithClickToEdit = Link.extend({
  addProseMirrorPlugins() {
    // Preserve the plugins the parent extension registers — autolink and
    // paste handling (the upstream click handler is skipped because
    // `openOnClick: false`; this extension owns click intent instead).
    const parentPlugins = this.parent?.() ?? []

    // Autolink is add-only and its mark is inclusive, so text edited under
    // an autolinked run keeps a stale href. The revalidation plugin runs
    // AFTER the parent's autolink so it sees the marks autolink just added
    // and re-normalises them along with everything the edit touched. Only
    // meaningful while autolink is on — that is also when the mark is
    // inclusive (`inclusive()` returns `options.autolink`).
    //
    // The punctuation plugin sits between the two: it links a URL that ends
    // a sentence (`example.com.`), which upstream declines because the word
    // as a whole is not a link, and it uses upstream's own allow-check.
    const { protocols, defaultProtocol } = this.options
    const revalidate = this.options.autolink
      ? [
          linkAutolinkPunctuation({
            type: this.type,
            defaultProtocol,
            validate: (url) =>
              this.options.isAllowedUri(url, {
                defaultValidate: (href) => !!isAllowedUri(href, protocols),
                protocols,
                defaultProtocol,
              }),
            shouldAutoLink: this.options.shouldAutoLink,
          }),
          linkAutolinkRevalidate({
            type: this.type,
            defaultProtocol: this.options.defaultProtocol,
          }),
        ]
      : []

    return [
      ...parentPlugins,
      ...revalidate,
      new Plugin({
        props: {
          handleClick(view, pos, event) {
            // Only handle the plain left-click case. Cmd/Ctrl+click
            // (open-in-new-tab) and middle-click stay native so the
            // user keeps the existing "click to open in new tab" path
            // via modifier keys. Right-click is also unaffected
            // (context menu is browser-managed).
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false
            if (event.button !== 0) return false

            // The click must land on the rendered <a> itself. This is the
            // whole guard against the "clicking the list item opens the
            // link editor" bug: the mark lookup below deliberately checks
            // BOTH sides of the resolved position, so a caret landing at
            // the link's TRAILING boundary reads as inside it — and that
            // boundary is exactly where a click in the empty space after
            // a link resolves to. A link at the end of a list item or a
            // paragraph therefore hijacked every click in the remaining
            // whitespace of its own block, which is why typing a trailing
            // space "fixed" it (the space becomes `nodeBefore`, carrying
            // no mark).
            //
            // Hit-testing the DOM instead of reasoning about positions
            // matches what the user actually did — position math can't
            // separate "caret ended up next to a link" from "I clicked
            // the link", but the anchor element can. The mark resolution
            // below still runs and is still load-bearing: it rejects the
            // other <a>s in the editor that are NOT link marks (wikilink
            // chips render as anchors too).
            if (!isClickOnLinkAnchor(event.target, view.dom)) return false

            // Resolve the link mark at the click pos. ProseMirror's
            // `nodeAfter` / `nodeBefore` get the inline node either side
            // of the resolved position — when the click lands inside a
            // text node carrying a link mark, one of them carries it.
            // Walk both sides to support edge clicks (right at the
            // mark's start/end boundary).
            const $pos = view.state.doc.resolve(pos)
            const linkType = view.state.schema.marks.link
            if (!linkType) return false
            const markAfter = $pos.nodeAfter?.marks.find((m) => m.type === linkType)
            const markBefore = $pos.nodeBefore?.marks.find((m) => m.type === linkType)
            const linkMark = markAfter ?? markBefore
            if (!linkMark) return false

            const existingHref = typeof linkMark.attrs.href === 'string' ? linkMark.attrs.href : ''
            if (!existingHref) return false

            // Compute the doc range covered by this mark. The standard
            // recipe walks the resolved-position's parent to find the
            // contiguous run of inline children sharing the same mark.
            const parent = $pos.parent
            const parentStart = $pos.start()
            let markFrom = pos
            let markTo = pos
            let offset = 0
            parent.forEach((child, childOffset) => {
              const childFrom = parentStart + childOffset
              const childTo = childFrom + child.nodeSize
              const hasMark = child.marks.some((m) => m.type === linkType && m.eq(linkMark))
              if (hasMark && childFrom <= pos && childTo >= pos) {
                markFrom = childFrom
                markTo = childTo
              }
              // Extend leftward — walk preceding siblings carrying the
              // same mark.
              if (hasMark && childTo === markFrom) {
                markFrom = childFrom
              }
              // Extend rightward — same direction on the trailing side.
              if (hasMark && childFrom === markTo) {
                markTo = childTo
              }
              offset = childOffset + child.nodeSize
            })
            // `offset` is referenced to satisfy biome's noUnusedVars
            // rule even though the loop body does the real work; remove
            // when biome allows local-only loop helpers.
            void offset

            // Belt-and-braces sweep: walk forward + backward from the
            // initial range until the adjacent inline char no longer
            // carries the same href. Catches the case where the mark
            // spans across text-node boundaries within a single
            // paragraph (different text nodes adjacent in the inline
            // sequence — common when marks compose with strong / em).
            const docSize = view.state.doc.content.size
            while (markFrom > 0) {
              const $prev = view.state.doc.resolve(markFrom - 1)
              const prevMark = $prev.nodeAfter?.marks.find((m) => m.type === linkType)
              if (!prevMark || prevMark.attrs.href !== existingHref) break
              markFrom -= 1
            }
            while (markTo < docSize) {
              const $next = view.state.doc.resolve(markTo)
              const nextMark = $next.nodeAfter?.marks.find((m) => m.type === linkType)
              if (!nextMark || nextMark.attrs.href !== existingHref) break
              markTo += 1
            }

            // Anchor rect — the bounding box of the clicked text. The
            // popover positions itself underneath; the bridge uses
            // coordsAtPos for both ends of the mark and unions them.
            let anchorRect: DOMRect | null = null
            try {
              const start = view.coordsAtPos(markFrom)
              const end = view.coordsAtPos(markTo)
              const top = Math.min(start.top, end.top)
              const bottom = Math.max(start.bottom, end.bottom)
              const left = Math.min(start.left, end.left)
              const right = Math.max(start.right, end.right)
              anchorRect = new DOMRect(left, top, right - left, bottom - top)
            } catch {
              anchorRect = null
            }
            if (!anchorRect) return false

            event.preventDefault()
            window.dispatchEvent(
              new CustomEvent<LinkClickPopoverEventDetail>(OPEN_LINK_POPOVER_EVENT, {
                detail: {
                  source: 'link-click',
                  existingHref,
                  markFrom,
                  markTo,
                  anchorRect: {
                    left: anchorRect.left,
                    top: anchorRect.top,
                    right: anchorRect.right,
                    bottom: anchorRect.bottom,
                  },
                },
              }),
            )
            return true
          },
        },
      }),
    ]
  },
})
